import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ApiError } from '../api/client'
import { postCall, postResend, type MemberRecord } from './api'
import { detailsErrorText } from './details'
import { paymentHref, printHref } from './links'
import { receiptChoices, resendDisabledReason, resendItems, resendTitles, sentMessage, type ResendWhat } from './quickActions'

// The row of small actions under the member header: a payment, a call, Resend and Print.
const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const inp = `min-h-[40px] rounded-lg border border-ink-400 bg-ink-800 px-3 text-paper ${focus}`
const lbl = 'flex flex-col gap-1.5 text-[13px] font-semibold text-paper/70'
const btn = `inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-ink-400 px-3 text-[13px] font-semibold text-paper hover:bg-ink-600 ${focus}`
const DIRS = [['in', 'They called us'], ['out', 'We called them']] as const

function CallForm({ m, onDone, onCancel }: { m: MemberRecord; onDone: (forwarded: boolean) => void; onCancel: () => void }) {
  const [direction, setDirection] = useState<'in' | 'out'>('in')
  const [slug, setSlug] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const save = useMutation({
    mutationFn: (forward: boolean) => postCall(m.id, { direction, publication_slug: slug || null, note: note.trim(), forward }),
    onSuccess: (_r, forward) => onDone(forward),
    onError: (e) => setError(e instanceof ApiError ? detailsErrorText(e.status, e.detail) : 'Could not save. Check your connection and try again.'),
  })
  // A Support desk conversation belongs to one title: the picked one, else the member's only one.
  const held = m.titles.filter((t) => t.state !== 'never')
  const forward = () => {
    setError(null)
    if (!slug && held.length !== 1) { setError('Pick the title this call is about before forwarding it.'); return }
    save.mutate(true)
  }
  return (
    <form
      aria-labelledby="call-h"
      className="flex flex-col gap-4 rounded-xl border border-ink-500 bg-ink-700 p-5"
      onSubmit={(e) => { e.preventDefault(); setError(null); if (note.trim()) save.mutate(false) }}
    >
      <h2 id="call-h" className="font-serif text-[20px]">Log a phone call</h2>
      <div className="flex flex-wrap items-center gap-3.5">
        <span role="radiogroup" aria-label="Who called" className="inline-flex overflow-hidden rounded-lg border border-ink-400">
          {DIRS.map(([k, label]) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={direction === k}
              onClick={() => setDirection(k)}
              className={`min-h-[36px] px-3 text-[13px] font-semibold ${focus} ${direction === k ? 'bg-paper text-ink-900' : 'text-paper/70 hover:bg-ink-600'}`}
            >
              {label}
            </button>
          ))}
        </span>
        <label className="inline-flex items-center gap-2 text-[13px] font-semibold text-paper/70">
          About
          <select className={`${inp} w-[220px]`} value={slug} onChange={(e) => setSlug(e.target.value)}>
            <option value="">Any title</option>
            {m.titles.map((t) => <option key={t.slug} value={t.slug}>{t.name}</option>)}
          </select>
        </label>
      </div>
      <label className={lbl}>
        What was said
        <textarea className={`${inp} py-2`} rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="One or two lines is plenty" />
      </label>
      {error && <p role="alert" className="m-0 text-red-500">{error}</p>}
      <div className="flex flex-wrap gap-2">
        <button type="submit" disabled={!note.trim() || save.isPending} className={`inline-flex min-h-[40px] items-center rounded-lg bg-paper px-4 font-bold text-ink-900 disabled:opacity-50 ${focus}`}>
          {save.isPending ? 'Saving…' : 'Save to history'}
        </button>
        <button type="button" onClick={forward} disabled={!note.trim() || save.isPending}
          title="Saves the call to history and puts it in the Customer Support queue for this title"
          className={`${btn} min-h-[40px] disabled:opacity-50`}>
          Save &amp; forward to customer service
        </button>
        <button type="button" className={btn} onClick={onCancel}>Cancel</button>
      </div>
    </form>
  )
}

const menuBox = 'absolute left-0 top-full z-20 mt-1 flex min-w-[240px] flex-col rounded-lg border border-ink-400 bg-ink-700 p-1 shadow-lg'
const menuItem = `rounded-md px-3 py-2 text-left text-[13px] font-semibold text-paper hover:bg-ink-600 ${focus}`

/** Resend: pick what, then (when the member has several titles) which title. */
function ResendMenu({ m, onSent, onError }: { m: MemberRecord; onSent: (msg: string) => void; onError: (msg: string) => void }) {
  const [open, setOpen] = useState(false)
  const [what, setWhat] = useState<ResendWhat | null>(null)
  const reason = resendDisabledReason(m)
  const titles = resendTitles(m)
  const send = useMutation({
    mutationFn: ({ w, slug }: { w: ResendWhat; slug: string }) => postResend(m.id, { what: w, publication_slug: slug }),
    onSuccess: (r, v) => onSent(sentMessage(v.w, r.sent_to)),
    onError: (e) => onError(e instanceof ApiError ? detailsErrorText(e.status, e.detail) : 'Could not send. Check your connection and try again.'),
  })
  const close = () => { setOpen(false); setWhat(null) }
  const go = (w: ResendWhat, slug: string) => { close(); send.mutate({ w, slug }) }
  return (
    <div className="relative">
      <button
        type="button" className={`${btn} disabled:cursor-not-allowed disabled:opacity-50`} aria-haspopup="menu" aria-expanded={open}
        disabled={!!reason || send.isPending} title={reason ?? undefined} onClick={() => { setOpen((o) => !o); setWhat(null) }}
      >
        {send.isPending ? 'Sending…' : 'Resend ▾'}
      </button>
      {reason && <span className="sr-only">{reason}</span>}
      {open && (
        <div role="menu" aria-label="Resend" className={menuBox} onKeyDown={(e) => { if (e.key === 'Escape') close() }}>
          {what === null
            ? resendItems(m).map((i) => (
                <button key={i.what} type="button" role="menuitem" className={menuItem}
                  onClick={() => (titles.length === 1 ? go(i.what, titles[0].slug) : setWhat(i.what))}>{i.label}</button>
              ))
            : (
              <>
                <span className="px-3 py-1 text-[12px] text-paper/60">Which title?</span>
                {titles.map((t) => <button key={t.slug} type="button" role="menuitem" className={menuItem} onClick={() => go(what, t.slug)}>{t.name}</button>)}
              </>
            )}
        </div>
      )}
    </div>
  )
}

/** Print: opens the print page in a new tab. A receipt needs an order, so it asks which. */
function PrintMenu({ m }: { m: MemberRecord }) {
  const [open, setOpen] = useState(false)
  const [receipts, setReceipts] = useState(false)
  const choices = receiptChoices(m)
  const close = () => { setOpen(false); setReceipts(false) }
  const linkProps = { role: 'menuitem', target: '_blank', rel: 'noopener', className: menuItem, onClick: close } as const
  return (
    <div className="relative">
      <button type="button" className={btn} aria-haspopup="menu" aria-expanded={open} onClick={() => { setOpen((o) => !o); setReceipts(false) }}>Print ▾</button>
      {open && (
        <div role="menu" aria-label="Print" className={menuBox} onKeyDown={(e) => { if (e.key === 'Escape') close() }}>
          {receipts ? (
            choices.map((c) => <a key={c.order_key} href={`${printHref(m.id, 'receipt')}?order_key=${encodeURIComponent(c.order_key)}`} {...linkProps}>{c.label}</a>)
          ) : (
            <>
              <button type="button" role="menuitem" className={`${menuItem} disabled:opacity-50`} disabled={choices.length === 0}
                title={choices.length === 0 ? 'No payment on record' : undefined}
                onClick={() => (choices.length === 1 ? (window.open(`${printHref(m.id, 'receipt')}?order_key=${encodeURIComponent(choices[0].order_key)}`, '_blank', 'noopener'), close()) : setReceipts(true))}>
                Receipt letter
              </button>
              <a href={printHref(m.id, 'label')} {...linkProps}>Address label</a>
              <a href={printHref(m.id, 'summary')} {...linkProps}>Member summary</a>
            </>
          )}
        </div>
      )}
    </div>
  )
}

export function QuickActions({ m, onLogged }: { m: MemberRecord; onLogged: () => void }) {
  const [callOpen, setCallOpen] = useState(false)
  const [toast, setToast] = useState('')
  const [toastBad, setToastBad] = useState(false)
  return (
    <>
      <div className="-mt-3 flex flex-wrap items-center gap-2">
        <Link to={paymentHref(m.id)} className={`inline-flex min-h-[36px] items-center gap-1.5 rounded-lg bg-paper px-3 text-[13px] font-bold text-ink-900 ${focus}`}>Record a payment</Link>
        <button type="button" className={btn} aria-expanded={callOpen} onClick={() => { setCallOpen((o) => !o); setToast('') }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" /></svg>
          Log a phone call
        </button>
        <ResendMenu m={m} onSent={(msg) => { setToast(msg); setToastBad(false) }} onError={(msg) => { setToast(msg); setToastBad(true) }} />
        <PrintMenu m={m} />
        <span role="status" className={`text-[13px] ${toastBad ? 'text-red-500' : 'text-green-500'}`}>{toast}</span>
      </div>
      {callOpen && (
        <CallForm
          m={m}
          onCancel={() => setCallOpen(false)}
          onDone={(forwarded) => {
            setCallOpen(false)
            setToast(forwarded ? 'Call saved and forwarded to customer service.' : 'Call saved to history.')
            setToastBad(false)
            onLogged()
          }}
        />
      )}
    </>
  )
}
